import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import * as api from "../api/cart";

const CartContext = createContext(null);

// Global toast event for cart messages
const showCartToast = (message, type = "success", action = null) => {
  window.dispatchEvent(
    new CustomEvent("show-toast", { detail: { message, type, action } }),
  );
};

export function CartProvider({ children }) {
  const { token, user } = useAuth();
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cart") || "[]");
    } catch (e) {
      return [];
    }
  });

  // ensure guestId exists for unauthenticated users
  useEffect(() => {
    try {
      let g = localStorage.getItem("guestId");
      if (!g) {
        g = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem("guestId", g);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cart));
    } catch (e) {}
  }, [cart]);

  // keep server in sync: when token/user changes, fetch server cart and merge
  useEffect(() => {
    let mounted = true;

    const syncCart = async (opts = {}) => {
      try {
        const guestId = localStorage.getItem("guestId");
        // if logged in, try to merge guest cart first
        if (opts.token) {
          if (guestId) await api.mergeCart({ guestId, token: opts.token });
          const res = await api.getCart({ token: opts.token });
          console.debug("CartContext: fetched user cart", { guestId, res });
          if (!mounted) return;
          if (res && res.items) {
            const mapped = res.items.map((i) => {
              const prod = i.product || {};
              return {
                _id: i._id,
                product: prod,
                qty: i.quantity || 1,
                price: i.priceAtAdd,
                pricing: prod.pricing || undefined,
                name:
                  prod.basic?.productName || prod.name || i.productName || "",
                photo:
                  prod.media?.mainImage || prod.photo || i.productPhoto || "",
              };
            });
            setCart(mapped);
          }
        } else {
          // guest
          const guestIdLocal = guestId;
          if (!guestIdLocal) return;
          const res = await api.getCart({ guestId: guestIdLocal });
          console.debug("CartContext: fetched guest cart", {
            guestId: guestIdLocal,
            res,
          });
          if (!mounted) return;
          if (res && res.items) {
            const mapped = res.items.map((i) => {
              const prod = i.product || {};
              return {
                _id: i._id,
                product: prod,
                qty: i.quantity || 1,
                price: i.priceAtAdd,
                pricing: prod.pricing || undefined,
                name:
                  prod.basic?.productName || prod.name || i.productName || "",
                photo:
                  prod.media?.mainImage || prod.photo || i.productPhoto || "",
              };
            });
            setCart(mapped);

            // if server returned empty but client has local cart, try to re-sync once per session
            try {
              const localRaw = localStorage.getItem("cart");
              const localArr = localRaw ? JSON.parse(localRaw) : [];
              const didResync = sessionStorage.getItem("cartResyncDone");
              if (
                (!res.items || res.items.length === 0) &&
                Array.isArray(localArr) &&
                localArr.length > 0 &&
                !didResync
              ) {
                sessionStorage.setItem("cartResyncDone", "1");
                (async () => {
                  try {
                    const guestIdLocal2 = localStorage.getItem("guestId");
                    for (const it of localArr) {
                      const pid = it.product?._id || it._id || it.id;
                      if (!pid) continue;
                      await api.addToCart({
                        productId: pid,
                        quantity: it.qty || it.quantity || 1,
                        guestId: guestIdLocal2,
                      });
                    }
                    const refreshed = await api.getCart({
                      guestId: localStorage.getItem("guestId"),
                    });
                    if (
                      refreshed &&
                      refreshed.items &&
                      refreshed.items.length > 0
                    ) {
                      const mapped2 = refreshed.items.map((i) => {
                        const prod = i.product || {};
                        return {
                          _id: i._id,
                          product: prod,
                          qty: i.quantity || 1,
                          price: i.priceAtAdd,
                          pricing: prod.pricing || undefined,
                          name:
                            prod.basic?.productName ||
                            prod.name ||
                            i.productName ||
                            "",
                          photo:
                            prod.media?.mainImage ||
                            prod.photo ||
                            i.productPhoto ||
                            "",
                        };
                      });
                      setCart(mapped2);
                    }
                  } catch (e) {
                    console.error("cart resync failed", e);
                  }
                })();
              }
            } catch (e) {}
          }
        }
      } catch (err) {
        console.error("Cart sync error", err);
      }
    };

    // initial sync
    syncCart({ token });

    // Listen for explicit login events to sync immediately
    const onAuthLogin = (e) => {
      syncCart({
        token: e && e.detail && e.detail.token ? e.detail.token : token,
      });
    };
    window.addEventListener("auth:login", onAuthLogin);

    return () => {
      mounted = false;
      window.removeEventListener("auth:login", onAuthLogin);
    };
  }, [token, user]);

  const addToCart = (p) => {
    (async () => {
      try {
        const guestId = localStorage.getItem("guestId");
        const payload = {
          productId: p._id || p.id,
          quantity: 1,
          variantId: p.variantId,
          guestId,
        };
        const res = await api.addToCart({ ...payload, token });
        if (res && res.item) {
          // GA4 add_to_cart event
          const prod = p;
          window.gtag &&
            window.gtag("event", "add_to_cart", {
              currency: "INR",
              value: prod.pricing?.price,
              section: prod.section || undefined,
              items: [
                {
                  item_id: prod._id,
                  item_name: prod.basic?.productName || prod.name,
                  price: prod.pricing?.price,
                  item_category: prod.category?.name,
                  section: prod.section || undefined,
                },
              ],
            });
          // refresh full cart from server to get populated product data
          try {
            const refreshed = await api.getCart({ guestId, token });
            if (refreshed && refreshed.items) {
              const mapped = refreshed.items.map((i) => {
                const prod = i.product || {};
                return {
                  _id: i._id,
                  product: prod,
                  qty: i.quantity || 1,
                  price: i.priceAtAdd,
                  pricing: prod.pricing || undefined,
                  name:
                    prod.basic?.productName || prod.name || i.productName || "",
                  photo:
                    prod.media?.mainImage || prod.photo || i.productPhoto || "",
                };
              });
              setCart(mapped);
            }
          } catch (err) {
            // fallback to using returned item
            setCart((prev) => {
              const itemId = res.item._id || res.item.id;
              const found = prev.find((x) => String(x._id) === String(itemId));
              const newItem = {
                ...res.item,
                _id: itemId,
                qty: res.item.quantity || 1,
                selected: true,
              };
              if (found)
                return prev.map((x) =>
                  String(x._id) === String(itemId)
                    ? { ...x, qty: newItem.qty }
                    : x,
                );
              return [...prev, newItem];
            });
          }

          // Show success toast with "View Cart" button
          showCartToast("Added to cart successfully!", "success", {
            label: "",
            path: "",
          });
        }
      } catch (err) {
        console.error("addToCart sync error", err);
        // fallback to client-only behaviour
        setCart((prev) => {
          // ensure a stable id for the cart item
          const itemId =
            p._id ||
            p.id ||
            p.basic?.internalSKU ||
            `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
          const found = prev.find((x) => x._id === itemId);
          if (found)
            return prev.map((x) =>
              x._id === itemId ? { ...x, qty: (x.qty || 1) + 1 } : x,
            );
          const toAdd = { ...p, _id: itemId, qty: 1, selected: true };
          return [...prev, toAdd];
        });
      }
    })();
  };

  const updateQuantity = (id, qty) => {
    if (!id) return;
    const n = Number(qty) || 1;
    const qtyClamped = n < 1 ? 1 : n;
    (async () => {
      try {
        const guestId = localStorage.getItem("guestId");
        const tokenLocal = token;
        await api.updateQuantity({
          id,
          quantity: qtyClamped,
          guestId,
          token: tokenLocal,
        });
      } catch (err) {
        console.error("updateQuantity sync error", err);
      }
      setCart((prev) =>
        prev.map((x) => (x._id === id ? { ...x, qty: qtyClamped } : x)),
      );
    })();
  };

  const toggleSelect = (id) => {
    if (!id) return;
    setCart((prev) =>
      prev.map((x) => {
        if (x._id !== id) return x;
        // item is considered included by default when `selected` is not present
        const currentlyIncluded = "selected" in x ? !!x.selected : true;
        return { ...x, selected: !currentlyIncluded };
      }),
    );
  };

  const selectAll = (selected = true) => {
    setCart((prev) => prev.map((x) => ({ ...x, selected })));
  };

  const removeFromCart = (id) => {
    if (!id) return;
    (async () => {
      try {
        const guestId = localStorage.getItem("guestId");
        await api.removeItem({ id, guestId, token });
      } catch (err) {
        console.error("removeFromCart sync error", err);
      }
      setCart((prev) => prev.filter((x) => x._id !== id));
    })();
  };
  const clearCart = () => setCart([]);
  const count = cart.reduce((s, x) => s + (x.qty || 0), 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        toggleSelect,
        selectAll,
        removeFromCart,
        clearCart,
        count,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
